import React, { useEffect, useState } from "react";
import { whatsappLink } from "../lib/whatsapp";
import { useI18n } from "../lib/i18n";

/**
 * Floating WhatsApp Business button — bottom-right.
 * Uses the official WhatsApp glyph (SVG) so it reads instantly as
 * "WhatsApp" rather than a generic chat bubble.
 * Mobile-first: enlarged tap target, ambient glow, soft entrance.
 */
export const FloatingWhatsApp = () => {
  const { locale } = useI18n();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 240);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const tip = locale === "de" ? "Auf WhatsApp schreiben" : "Message on WhatsApp";
  const presetMsg =
    locale === "de"
      ? "Hallo Joshi FusionTech Media, ich interessiere mich für eure Dienstleistungen."
      : "Hi Joshi FusionTech Media, I'm interested in your services.";

  return (
    <a
      href={whatsappLink(presetMsg)}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="floating-whatsapp"
      aria-label={tip}
      title={tip}
      className={`
        group fixed bottom-6 right-[88px] sm:right-[96px] z-40
        flex items-center justify-center
        rounded-full
        bg-[#25D366] text-white
        shadow-[0_10px_28px_rgba(37,211,102,0.40)]
        ring-1 ring-white/15
        transition-all duration-300 ease-out
        hover:scale-110 hover:shadow-[0_18px_44px_rgba(37,211,102,0.65)]
        focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#25D366]/40
        ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"}
      `}
      style={{ width: 56, height: 56 }}
    >
      {/* Ambient pulse halo */}
      <span
        aria-hidden
        className="absolute inset-0 rounded-full bg-[#25D366] opacity-40 group-hover:opacity-60 animate-ping"
        style={{ animationDuration: "2.4s" }}
      />
      {/* Official WhatsApp glyph */}
      <svg
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
        className="relative w-7 h-7 drop-shadow-[0_1px_2px_rgba(0,0,0,0.18)]"
        aria-hidden="true"
      >
        <path
          fill="currentColor"
          d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.501-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488"
        />
      </svg>
      <span className="sr-only">{tip}</span>
    </a>
  );
};
